// Catalog picker for the admin new-listing modal: make → model → (optional
// year) → entry. Ports the crew storefront CatalogPicker behaviour, but uses
// TanStack Query against the /admin/catalog/* fetchers instead of Next server
// actions, and backoffice styling instead of the brand theme.
//
// When an entry is resolved it reports { id, title } up via onEntryResolved —
// the modal uses the id as the required `carId` and the title to prefill the
// listing title. A lone trim (single entry for the model/year) is auto-resolved
// so the operator never opens a one-option Trim select.

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  fetchCatalogPickerEntries,
  fetchCatalogPickerMakes,
  fetchCatalogPickerModels,
  fetchCatalogPickerYears,
} from '@/lib/admin-api';

const field =
  'h-9 w-full rounded-lg border border-[color:var(--bo-line)] bg-white px-3 text-[13px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-mute)] disabled:opacity-50';
const lbl = 'text-[12px] font-medium text-[color:var(--bo-ink)]';

export function CatalogPicker({
  onEntryResolved,
  disabled = false,
}: {
  onEntryResolved: (picked: { id: number; title: string }) => void;
  disabled?: boolean;
}) {
  const [makeId, setMakeId] = useState<number | null>(null);
  const [modelId, setModelId] = useState<number | null>(null);
  const [year, setYear] = useState<number | null>(null);
  const [selectedEntryId, setSelectedEntryId] = useState<number | null>(null);
  const [selectedTitle, setSelectedTitle] = useState<string | null>(null);

  const makesQ = useQuery({
    queryKey: ['admin', 'catalog', 'makes'],
    queryFn: ({ signal }) => fetchCatalogPickerMakes(signal),
  });

  const modelsQ = useQuery({
    queryKey: ['admin', 'catalog', 'models', makeId],
    queryFn: ({ signal }) => fetchCatalogPickerModels(makeId!, signal),
    enabled: makeId != null,
  });

  const yearsQ = useQuery({
    queryKey: ['admin', 'catalog', 'years', modelId],
    queryFn: ({ signal }) => fetchCatalogPickerYears(modelId!, signal),
    enabled: modelId != null,
  });
  const years = yearsQ.data ?? [];
  // Catalog entries seeded from listings carry no year, so a model can have zero
  // structured years — in that case we skip the Year step and load its trims
  // directly. Only require a year when the model actually has structured years.
  const needsYear = years.length > 0;

  const entriesQ = useQuery({
    queryKey: ['admin', 'catalog', 'entries', modelId, year],
    queryFn: ({ signal }) => fetchCatalogPickerEntries(modelId!, year, signal),
    // Wait for years to resolve before fetching entries, so we know whether the
    // model has structured years (→ require a year) or not (→ year-less entries).
    enabled: modelId != null && yearsQ.isSuccess && (!needsYear || year != null),
  });
  const entries = entriesQ.data ?? [];
  // Only trust `entries` once the query has actually resolved — while it is
  // loading `entries` is [] , which is indistinguishable from "this model has
  // no catalog entries" and would flash the empty-state warning below.
  const entriesReady = entriesQ.isSuccess;
  // The submit button is gated on a resolved entry (`carId`). Every state where
  // the operator has picked a model but NO entry has resolved needs to say so —
  // otherwise "Create listing" is disabled with nothing on screen explaining
  // why. That was the reported bug: a multi-trim model (e.g. Hyundai Tucson)
  // renders the Trim select unselected and the form looks complete.
  const modelChosen = modelId != null;
  const yearSatisfied = !needsYear || year != null;
  const awaitingTrim = modelChosen && yearSatisfied && entriesReady && entries.length > 1 && selectedEntryId == null;
  const noEntries = modelChosen && yearSatisfied && entriesReady && entries.length === 0;

  // Auto-resolve a lone trim. Guarded on the id so re-renders (or a new
  // onEntryResolved identity) don't re-fire for an already-selected entry.
  useEffect(() => {
    const only = entries.length === 1 ? entries[0] : undefined;
    if (only && only.id !== selectedEntryId) {
      const title = only.trim || only.defaultTitle;
      setSelectedEntryId(only.id);
      setSelectedTitle(title);
      onEntryResolved({ id: only.id, title });
    }
  }, [entries, selectedEntryId, onEntryResolved]);

  function onMake(v: number | null) {
    setMakeId(v);
    setModelId(null);
    setYear(null);
    setSelectedEntryId(null);
    setSelectedTitle(null);
  }

  function onModel(v: number | null) {
    setModelId(v);
    setYear(null);
    setSelectedEntryId(null);
    setSelectedTitle(null);
  }

  function onYear(v: number | null) {
    setYear(v);
    setSelectedEntryId(null);
    setSelectedTitle(null);
  }

  function onTrim(entryId: number | null) {
    if (!entryId) {
      setSelectedEntryId(null);
      setSelectedTitle(null);
      return;
    }
    const entry = entries.find((e) => e.id === entryId);
    if (!entry) return;
    const title = entry.trim || entry.defaultTitle;
    setSelectedEntryId(entry.id);
    setSelectedTitle(title);
    onEntryResolved({ id: entry.id, title });
  }

  const makes = makesQ.data ?? [];
  const models = modelsQ.data ?? [];

  return (
    <fieldset
      className="grid grid-cols-2 gap-2 rounded-lg border border-[color:var(--bo-accent)]/25 bg-[color:var(--bo-accent)]/5 p-3"
      disabled={disabled}
    >
      <legend className="col-span-2 px-1 text-[12px] font-semibold text-[color:var(--bo-accent)]">
        Vehicle (catalog) *
      </legend>

      <label className="block space-y-1">
        <span className={lbl}>Make</span>
        <select
          aria-label="Make"
          value={makeId ?? ''}
          onChange={(e) => onMake(e.target.value ? Number(e.target.value) : null)}
          className={field}
        >
          <option value="">{makesQ.isLoading ? 'Loading…' : 'Select make'}</option>
          {makes.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
        </select>
      </label>

      <label className="block space-y-1">
        <span className={lbl}>Model</span>
        <select
          aria-label="Model"
          value={modelId ?? ''}
          disabled={makeId == null}
          onChange={(e) => onModel(e.target.value ? Number(e.target.value) : null)}
          className={field}
        >
          <option value="">{modelsQ.isLoading ? 'Loading…' : 'Select model'}</option>
          {models.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
        </select>
      </label>

      {/* Year only appears when the model has structured years — entries seeded
          from listings carry none, so an empty box would read as broken. */}
      {needsYear ? (
        <label className="block space-y-1">
          <span className={lbl}>Year</span>
          <select
            aria-label="Year"
            value={year ?? ''}
            disabled={modelId == null}
            onChange={(e) => onYear(e.target.value ? Number(e.target.value) : null)}
            className={field}
          >
            <option value="">Select year</option>
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      {/* Trim only appears with more than one entry — a lone entry is
          auto-resolved by the effect above. */}
      {entriesReady && entries.length > 1 ? (
        <label className="block space-y-1">
          <span className={lbl}>Trim</span>
          <select
            aria-label="Trim"
            value={selectedEntryId ?? ''}
            onChange={(e) => onTrim(e.target.value ? Number(e.target.value) : null)}
            className={field}
          >
            <option value="">Select trim</option>
            {entries.map((en) => (
              <option key={en.id} value={en.id}>
                {en.trim || en.defaultTitle}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      {selectedTitle ? (
        <p className="col-span-2 text-[11.5px] text-[color:var(--bo-accent)]">
          Selected: <strong>{selectedTitle}</strong>
        </p>
      ) : null}

      {/* Why is "Create listing" still disabled? Say it out loud. Without these
          the form reads as complete while `carId` is null. */}
      {awaitingTrim ? (
        <p className="col-span-2 text-[11.5px] text-[#9a6b00]">
          Pick a <strong>Trim</strong> to finish choosing the vehicle — the listing can&apos;t be
          created until one is selected.
        </p>
      ) : null}

      {noEntries ? (
        <p className="col-span-2 text-[11.5px] text-[#9f1239]">
          This model has no catalog entries{needsYear && year != null ? ` for ${year}` : ''}, so
          there is nothing to list. Add one in <strong>Vehicle Catalog</strong>, or pick a
          different model.
        </p>
      ) : null}
    </fieldset>
  );
}
